import { can, type Me } from '../auth/me';
import type { Confirmation } from './confirmation';
import { addDays, warsawInstant, warsawParts } from '../events/labels';
import { FUNCTIONS_RESOURCE, PERSONS_RESOURCE, type Person } from '../people/types';

export const CREW_RESOURCE = 'crew.assignments';
export const CREW_KEY = ['crew'] as const;

export const ASSIGNMENT_STATUSES = { proponowany: 'Proponowany', potwierdzony: 'Potwierdzony', odmowa: 'Odmowa' } as const;
export type AssignmentStatus = keyof typeof ASSIGNMENT_STATUSES;

/** An assignment as the API sends it: fields the role may not see are absent (rule 7a). */
export type Assignment = {
  id: string;
  eventId: string;
  version: number;
  personId?: string;
  personName?: string;
  functionId?: string;
  functionName?: string;
  startsAt?: string | null;
  endsAt?: string | null;
  status?: AssignmentStatus;
  notes?: string;
  /** „Potwierdź ręcznie” (owner 2026-10-28 12:01): how a „Potwierdzony” was confirmed; absent — not known or hidden. */
  confirmation?: Confirmation;
  /** „Dostępność → Ludzie” 1b: the days that are not plain — inactive (another event took them) or a collision. */
  days?: AssignmentDay[];
  /** Owner's Q3: the event's dates changed — ask this person again. */
  datesChanged?: boolean;
  /** Stage 2: added (or found) after „tak” for every day of a question about this event. */
  availabilityConfirmed?: boolean;
  /** Company calendar: inactive on every day of the event (another event took them) — not shown as working. */
  active?: boolean;
};

/** One day of an assignment: `active` false — another event took it (`because`); `collision` — events it overlaps that day. */
export type AssignmentDay = { day: string; active: boolean; past?: boolean; unavailable?: boolean; because?: { eventId?: string; eventName: string }; collision?: Array<{ eventId?: string; eventName: string }> };

/** '2026-11-07' → '7.11'. */
export const dayShort = (day: string) => `${Number(day.slice(8, 10))}.${Number(day.slice(5, 7))}`;

/** "Jan Kowalski" → "JK", "Ola" → "O": the crew on a calendar bar. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter((w) => /\p{L}/u.test(w));
  if (words.length === 0) return '';
  const first = words[0]!;
  const last = words.length > 1 ? words[words.length - 1]! : '';
  return (first.charAt(0) + last.charAt(0)).toLocaleUpperCase('pl-PL');
}

const timeOnly = (iso: string) => warsawParts(iso).time;

/** "całe wydarzenie", "10:00–14:00" (same Warsaw day) or "7.11 22:00 – 8.11 02:00". */
export function hoursText(startsAt: string | null | undefined, endsAt: string | null | undefined): string {
  if (!startsAt || !endsAt) return 'całe wydarzenie';
  const s = warsawParts(startsAt);
  const e = warsawParts(endsAt);
  if (s.day === e.day || (e.time === '00:00' && e.day === addDays(s.day, 1))) return `${s.time}–${e.time === '00:00' && e.day !== s.day ? '24:00' : timeOnly(endsAt)}`;
  const short = (day: string) => `${Number(day.slice(8, 10))}.${Number(day.slice(5, 7))}`;
  return `${short(s.day)} ${s.time} – ${short(e.day)} ${e.time}`;
}

/** Day + "HH:mm" fields of the form → instants; an end earlier than the start goes to the next day (night work). */
export function hoursToInstants(day: string, from: string, to: string): { startsAt: string; endsAt: string } {
  const startsAt = warsawInstant(day, from);
  const endDay = to <= from ? addDays(day, 1) : day;
  return { startsAt, endsAt: warsawInstant(endDay, to) };
}

/** One person of the crew with their assignments in this event (one per function), in the server's order. */
export type CrewGroup = { key: string; personName: string; assignments: Assignment[] };

/** The crew list: one row per person, their functions one after another; a person hidden from the role stays alone. */
export function byPerson(list: Assignment[]): CrewGroup[] {
  const groups = new Map<string, CrewGroup>();
  for (const a of list) {
    const key = a.personId ?? `assignment:${a.id}`;
    const group = groups.get(key) ?? { key, personName: a.personName ?? 'Osoba', assignments: [] };
    group.assignments.push(a);
    groups.set(key, group);
  }
  return [...groups.values()];
}

/** "Dodaj osobę": the people who can do the function, and everybody else ("Pozostałe osoby"). */
export function candidates(persons: Person[], functionId: string): { withFunction: Person[]; others: Person[] } {
  if (functionId === '') return { withFunction: [], others: [] };
  const has = (p: Person) => (p.functions ?? []).some((f) => f.functionId === functionId);
  return { withFunction: persons.filter(has), others: persons.filter((p) => !has(p)) };
}

/** What the permission editor must grant before the role can pick people for the crew ("Zasób: Akcja"). */
export function missingGrants(me: Me): string[] {
  const needed: [string, string][] = [
    [PERSONS_RESOURCE, '„Osoby: Podgląd”'],
    [FUNCTIONS_RESOURCE, '„Funkcje: Podgląd”'],
  ];
  return needed.filter(([resource]) => !can(me, resource, 'view')).map(([, label]) => label);
}

/** The crew of an event on the company calendar: each person once (also in several functions), without refusals. */
export function crewNames(crew: { personId?: string; personName?: string; status?: string; active?: boolean }[]): string[] {
  const seen = new Map<string, string>();
  for (const a of crew) {
    // „Dostępność → Ludzie” 1b: inactive on every day of the event — they do another one
    if (a.status === 'odmowa' || a.active === false || !a.personName) continue;
    const key = a.personId ?? `name:${a.personName}`;
    if (!seen.has(key)) seen.set(key, a.personName);
  }
  return [...seen.values()];
}
